import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import MarketingShell from "@/components/MarketingShell";
import SentenceLines from "@/components/SentenceLines";
import JmlLink from "@/components/JmlLink";

const EASE = [0.4, 0, 0.2, 1];

const reveal = {
  initial: { opacity: 0, y: 12 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.3 },
  transition: { duration: 0.7, ease: EASE },
};

const QUESTIONS = [
  {
    q: "What is the Alephonic Mapping Method™?",
    a: (
      <>
        A system for turning Hebrew letters into music. Every letter is a character with its own
        place on the scale; the order and shape of a word set the order and shape of the notes.
        Same word, same map, every time — then a human plays it.{" "}
        <JmlLink className="transition-colors hover:text-[#FFD9C2]" style={{ color: "#FCA07A" }}>
          The long version is here
        </JmlLink>
      </>
    ),
  },
  {
    q: "Do I need to read Hebrew?",
    a: (
      <>
        A little — and there&apos;s a keyboard right there. You tap the letters, so you never have to
        type in Hebrew or know the spelling of a word you&apos;ve only heard.
      </>
    ),
  },
  {
    q: "Is this AI?",
    a: (
      <>
        No — and that goes for the app too. Nothing here is generated. The Alephonic Mapping
        Method{"\u2122"} is a system Dan built and then programmed himself, rule by rule, so when
        the app turns your word into notes it&apos;s applying <em style={{ color: "#F5EFE3", fontStyle: "normal" }}>his</em>{" "}
        mapping — not a model&apos;s guess at what might sound nice. What you get out of it came
        from him. And on the recordings, a human being sits at a piano and plays it.
      </>
    ),
  },
  {
    q: "Is this a nigun? Is it Jewish music?",
    a: (
      <>
        No. It&apos;s contemporary and spacious, anchored in the letters rather than in a tradition
        of melody. It comes out of Torah, but it doesn&apos;t sound like anything you&apos;d sing at
        a table.
      </>
    ),
  },
  {
    q: "How can I support the music?",
    a: (
      <>
        There&apos;s no label behind this. $5, $18 or $36 a month keeps the
        weekly Alephonics podcast coming, puts your name on the Patrons wall and
        in the credits of any album released while you&apos;re a patron, and gets
        you into the behind-the-scenes WhatsApp.{" "}
        <Link to="/patrons" className="transition-colors hover:text-[#FFD9C2]" style={{ color: "#FCA07A" }}>
          See the tiers &rarr;
        </Link>
      </>
    ),
  },
  {
    q: "Can I commission a piece?",
    a: (
      <>
        Yes — a name, a date, a verse, a person. Tell me who it&apos;s for and what it&apos;s for.{" "}
        <Link to="/commissions" className="transition-colors hover:text-[#FFD9C2]" style={{ color: "#FCA07A" }}>
          Start here &rarr;
        </Link>
      </>
    ),
  },
  {
    q: "Does Dan perform live?",
    a: (
      <>
        Yes. If you&apos;re looking for a performance, use the contact form and get in touch.
      </>
    ),
  },
];

export default function Faq() {
  return (
    <MarketingShell active="/faq">
      <motion.section
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: EASE }}
        data-testid="faq-hero"
      >
        <p className="text-[11px] uppercase mb-4" style={{ color: "#F2D589", letterSpacing: "0.28em" }}>
          FAQ
        </p>
        <h1
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontWeight: 300,
            fontSize: "var(--step-3)",
            letterSpacing: "-0.02em",
            lineHeight: 1.12,
            maxWidth: "24ch",
          }}
          data-testid="faq-headline"
        >
          <SentenceLines text="Reasonable questions. Straight answers." />
        </h1>
      </motion.section>

      <div className="mt-20 flex flex-col">
        {QUESTIONS.map((item, i) => (
          <motion.section
            key={item.q}
            {...reveal}
            className="py-10"
            style={{ borderTop: i === 0 ? "none" : "1px solid #241246" }}
            data-testid={`faq-item-${i}`}
          >
            <h2
              className="max-w-[38ch]"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, fontSize: "var(--step-1)" }}
            >
              {item.q}
            </h2>
            <p className="mt-5 max-w-[70ch] text-base sm:text-lg" style={{ color: "#C6BBA9", lineHeight: 1.7 }}>
              {item.a}
            </p>
          </motion.section>
        ))}
      </div>

      <motion.div {...reveal} className="mt-16" data-testid="faq-cta">
        <JmlLink
          className="inline-flex items-center justify-center whitespace-nowrap rounded-full px-7 h-12 text-xs uppercase transition-colors hover:bg-[#F2D589]/[0.12]"
          style={{
            backgroundColor: "transparent",
            color: "#F2D589",
            border: "1px solid #F2D589",
            letterSpacing: "0.18em",
            fontWeight: 500,
          }}
        >
          Hear a word or verse
        </JmlLink>
      </motion.div>
    </MarketingShell>
  );
}
